import { useTranslation } from "react-i18next";
import { FC, useContext, useMemo } from "react";
import {
    AuthContext,
    DropdownOption,
    usePoliciesPagesConfig,
    useRuntimeConfig,
} from "@finbackoffice/site-core";
import { ConfigContextV2 } from "contexts";
import { getLanguageNativeName } from "@finbackoffice/fe-core";
import { Svg } from "components/base/svg/Svg";
import { AssetsDirectory, Language, TranslationScopes } from "@finbackoffice/enums";
import Translate from "components/base/translate/Translate";
import { useParams } from "next/navigation";
import Button from "components/base/button/Button";
import PromotionsDefaultList from "components/bonuses/promotions/list/PromotionsDefaultList";
import ActiveLink from "components/base/active-link/ActiveLink";
import { RouteNames } from "utils/constants";
import { SidebarContext } from "../../Sidebar";
import styles from "./advanced-sidebar-footer.module.sass";
import AdvancedSidebarLanguage from "./AdvancedSidebarLanguage";
import CollapsingBlok from "./CollapsingBlok";
import AdvancedSidebarOddsFormat from "./AdvancedSidebarOddsFormat";

const AdvancedSidebarFooter: FC = () => {
    const VERSION = useRuntimeConfig("VERSION");
    const COMMON_SITE_CONFIGS = useRuntimeConfig("COMMON_SITE_CONFIGS");

    const { t } = useTranslation();
    const { lang: locale } = useParams();
    const { enabledPoliciesConfig } = usePoliciesPagesConfig();

    const { siteConfigReady, siteCurrentOddFormat } = useContext(ConfigContextV2);
    const { languages, oddsFormats, renderPoliciesLink } = useContext(SidebarContext);
    const { isUserLoggedIn } = useContext(AuthContext);

    const renderPromotionsLink = useMemo(
        () => (
            <ActiveLink
                href={`/${RouteNames.BONUSES}/${RouteNames.PROMOTIONS}`}
                activeClassName={styles.selected}>
                <Translate tid="pageName_bonuses_promotions" />
            </ActiveLink>
        ),
        [],
    );

    return (
        <>
            <div className={styles.separator} />
            <CollapsingBlok
                title={t("footer_bonuses")}
                icon={
                    <Svg
                        src={`/${AssetsDirectory.Mobile}/${AssetsDirectory.ProfileCompact}/profile-promotions.svg`}
                        className="svg-icon"
                        prefixAppName
                    />
                }>
                {renderPromotionsLink}
                <PromotionsDefaultList activeClassName={styles.selected} />
            </CollapsingBlok>
            <CollapsingBlok
                title={t("footer_help_center")}
                icon={
                    <Svg
                        src={`/${AssetsDirectory.Common}/${AssetsDirectory.Tablet}/${AssetsDirectory.Sidebar}/help-center.svg`}
                        className="svg-icon"
                    />
                }>
                {enabledPoliciesConfig.map(renderPoliciesLink)}
            </CollapsingBlok>
            <div className={styles.separator} />
            <CollapsingBlok
                title={getLanguageNativeName(locale as Language)}
                icon={
                    <Svg
                        src={`/${AssetsDirectory.Common}/${AssetsDirectory.Languages}/${locale}.svg`}
                        className="svg-icon"
                        prefixAppName
                    />
                }
                loading={!siteConfigReady}>
                {languages.map((lang: DropdownOption) => (
                    <AdvancedSidebarLanguage key={lang.value} language={lang} />
                ))}
            </CollapsingBlok>
            {COMMON_SITE_CONFIGS.contentVerticals.sport && (
                <CollapsingBlok
                    title={t(`oddsFormats_${siteCurrentOddFormat}`)}
                    loading={!siteConfigReady}
                    icon={
                        <Svg
                            src={`/${AssetsDirectory.Common}/${AssetsDirectory.Tablet}/${AssetsDirectory.Sidebar}/decimal.svg`}
                            className="svg-icon"
                        />
                    }>
                    {oddsFormats.map((format) => (
                        <AdvancedSidebarOddsFormat key={format.value} format={format} />
                    ))}
                </CollapsingBlok>
            )}

            {isUserLoggedIn &&
                COMMON_SITE_CONFIGS.chatService.enable &&
                !COMMON_SITE_CONFIGS.chatService.showAfterSignIn && (
                    <div className={styles.mainMenuItem}>
                        <Button type="button" variant="custom">
                            <Svg
                                src={`/${AssetsDirectory.Mobile}/${AssetsDirectory.ProfileCompact}/profile-livechat.svg`}
                                className="svg-icon"
                                prefixAppName
                            />
                            <p>
                                {t("account_menu_chat_service_heading", {
                                    ns: TranslationScopes.UserAccount,
                                })}
                            </p>
                        </Button>
                    </div>
                )}

            <Translate
                tid="footer_copyright_text"
                dangerous
                replace={{ app_name: COMMON_SITE_CONFIGS.appName }}>
                <p />
            </Translate>
            <div>{`© ${new Date().getFullYear()} ${COMMON_SITE_CONFIGS.appName}`}</div>
            <p className={styles.version}>
                <Translate tid="footer_version" replace={{ version: VERSION }} />
            </p>
        </>
    );
};

export default AdvancedSidebarFooter;
